import { Button } from "antd";
import React, { useEffect, useState } from "react";
import { Card } from "../../subscription/misc/Card";
import { Checkbox } from "./misc/Checkbox";
import { Progress } from "./misc/Progress";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button2 from "../../../components/elements/forms/Button2";
import img from "../../../constant";
import axiosInstance from "../../../utility/axiosInstance";
import { OrganizationAgreementCheckbox } from "./misc/OrganizationAgreementCheckbox";
import { toast } from "sonner";
// import { title } from "process";

type BusinessAcc = {
  id: string;
  name: string;
  address: string;
  registrationNo: string;
  contactEmail: string;
  contactPhone: string;
  representativeName: string;
  representativeEmail: string;
  representativeRole: string;
  challengeDescription: string;
  companySize: string;
  atsSystem: string;
  referralSource: string;
  createdAt: string;
  updatedAt: string;
};

const TermsAcceptance: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAccepted, setIsAccepted] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("Id");
  const tier = searchParams.get("tier");
  const frequency = searchParams.get("frequency");
  const directPlanTier = searchParams.get("directPlan");
  const includeSetupFee = searchParams.get("includeSetupFee");

  const [biz, setBusiness] = useState<BusinessAcc | null>(null);

  const getBusinessData = async () => {
    try {
      const organizationId = id;

      if (!organizationId) {
        console.warn("Organization ID not found");
        return;
      }

      const response = await axiosInstance.get(
        `/organizations/${organizationId}/slight`,
      );
      if (response.data?.status === "success" && response.data?.data) {
        setBusiness(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching business data:", err);
    }
  };

  useEffect(() => {
    getBusinessData();
  }, []);

  const organizationId = id; // This would typically come from your app state

  const handleUpdateSuccess = () => {
    toast.success(`Agreement updated successfully`);
    console.log("Agreement updated successfully");
    // You can add toast notifications or other success feedback here
  };

  const handleUpdateError = (error: any) => {
    toast.error(`Failed to update agreement`);
    console.error("Failed to update agreement:", error);
    // You can add error handling or toast notifications here
  };

  const termsContent = [
    {
      title: "Data Processing Addendum (DPA)",
      content: (
        <>
          <p className="text-gray-600 mb-4">
            This Data Processing Addendum ("DPA") is made effective as of{" "}
            {new Date().toLocaleDateString()} and forms part of the Master
            Services Agreement ("MSA") between 25th & Staffing ("Provider") and{" "}
            {biz?.name || "[Customer Name]"} ("Customer").
          </p>

          <h3 className="text-lg font-bold mb-2">1. Definitions</h3>
          <p className="mb-4">
            <strong>"Data Protection Laws"</strong> means all applicable data
            privacy laws, including the Nigeria Data Protection Act 2023 (NDPA),
            the EU General Data Protection Regulation 2016/679 (GDPR) (where
            applicable), and any other relevant regulations.
          </p>
          <p className="mb-4">
            <strong>
              "Personal Data", "Controller", "Processor", "Data Subject",
              "Personal Data Breach", "Processing", and "Supervisory Authority"
            </strong>{" "}
            have the meanings given in the applicable Data Protection Laws.
          </p>
          <p className="mb-4">
            <strong>"Customer Data"</strong> means all data provided by Customer
            to Provider in connection with the Services, including Personal
            Data.
          </p>

          <h3 className="text-lg font-bold mb-2">2. Roles and Scope</h3>
          <p className="mb-2">
            <strong>2.1. Roles:</strong> Customer is the Controller and Provider
            is the Processor of Personal Data.
          </p>
          <p className="mb-2">
            <strong>2.2. Details of Processing:</strong>
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>
              <strong>Subject Matter:</strong> Processing of Personal Data
              related to Customer's recruitment, hiring, onboarding, performance
              management, disciplinary, and offboarding activities.
            </li>
            <li>
              <strong>Duration:</strong> For the term of the MSA, plus any
              period required for data return/deletion post-termination.
            </li>
            <li>
              <strong>Nature and Purpose:</strong> To provide the 25th Suite
              Services, including processing resumes, candidate profiles,
              interview data, employee records, leave requests, performance
              reviews, disciplinary logs, and salary/offer workflows.
            </li>
            <li>
              <strong>Types of Personal Data:</strong> Name, contact details,
              employment history, education, skills, interview recordings,
              financial details (e.g., salary, bank details, BVN, NIN), and
              other data in resumes/profiles/records.
            </li>
            <li>
              <strong>Categories of Data Subjects:</strong> Job applicants,
              candidates, employees, former employees, and managers of Customer.
            </li>
          </ul>

          <h3 className="text-lg font-bold mb-2">
            3. Obligations of Provider (Processor)
          </h3>
          <p className="mb-2">Provider shall:</p>
          <p className="mb-2">
            <strong>3.1.</strong> Process Personal Data only on documented
            instructions from Customer (as set out in the MSA, this DPA, or
            Subscription Orders), unless required by law (in which case Provider
            will notify Customer in advance where possible).
          </p>
          <p className="mb-2">
            <strong>3.2.</strong> Implement appropriate technical and
            organizational measures to ensure security of Personal Data (as
            detailed in Annex 1: Security Measures).
          </p>
          <p className="mb-2">
            <strong>3.3.</strong> Ensure personnel processing Personal Data are
            bound by confidentiality obligations.
          </p>
          <p className="mb-2">
            <strong>3.4.</strong> Assist Customer (at Customer's cost) with Data
            Subject rights requests, DPIAs, and Supervisory Authority
            consultations.
          </p>
          <p className="mb-2">
            <strong>3.5.</strong> Notify Customer without undue delay (and no
            later than 72 hours) of any Personal Data Breach, providing details
            of the breach, likely consequences, and measures taken.
          </p>
          <p className="mb-2">
            <strong>3.6.</strong> At the end of the Services or upon Customer's
            request, delete or return all Personal Data (unless retention is
            required by law) and delete existing copies.
          </p>
          <p className="mb-4">
            <strong>3.7.</strong> Make available information necessary to
            demonstrate compliance and allow audits (including inspections) by
            Customer or an independent auditor, at Customer's cost, upon
            reasonable notice (no more than once per year).
          </p>

          <h3 className="text-lg font-bold mb-2">4. Sub-Processors</h3>
          <p className="mb-2">
            <strong>4.1.</strong> Provider may engage Sub-Processors provided it
            notifies Customer in advance and gives 30 days to object. Current
            Sub-Processors are listed in Annex 2.
          </p>
          <p className="mb-4">
            <strong>4.2.</strong> Provider shall impose the same data protection
            obligations on Sub-Processors and remains fully liable for their
            performance.
          </p>

          <h3 className="text-lg font-bold mb-2">5. International Transfers</h3>
          <p className="mb-4">
            Customer acknowledges that processing may involve transfers outside
            Nigeria. Provider shall ensure all transfers comply with Data
            Protection Laws using appropriate safeguards (e.g., Standard
            Contractual Clauses or equivalent).
          </p>

          <h3 className="text-lg font-bold mb-2">
            6. Data Usage & Continuous Improvement
          </h3>
          <p className="mb-4">
            Anonymized and aggregated data (e.g., interview trends, scores) may
            be used for improving the Services, provided no Personal Data is
            identifiable. Customers may opt out of data contribution by written
            notice.
          </p>

          <h3 className="text-lg font-bold mb-2">
            7. Customer Obligations (Controller)
          </h3>
          <p className="mb-2">Customer shall:</p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li>Ensure all Personal Data has a lawful basis for processing.</li>
            <li>
              Respond to Data Subject requests and notify Provider of any
              relevant instructions.
            </li>
            <li>
              Maintain records of processing activities as required by law.
            </li>
          </ul>

          <h3 className="text-lg font-bold mb-2">
            8. Breach & Indemnification
          </h3>
          <p className="mb-4">
            Each Party shall indemnify the other for losses arising from its
            non-compliance with this DPA or Data Protection Laws.
          </p>

          <h3 className="text-lg font-bold mb-2">
            9. Governing Law & Amendments
          </h3>
          <p className="mb-4">
            This DPA is governed by the laws of the Federal Republic of Nigeria.
            Amendments must be in writing and signed by both Parties.
          </p>

          <h3 className="text-lg font-bold mb-2">10. Termination</h3>
          <p className="mb-8">
            Upon termination of the MSA, Provider shall cease processing and
            return/delete Personal Data as per Section 3.6.
          </p>

          <div className="border-t pt-4 mt-8">
            <h3 className="text-lg font-bold mb-4">Signatures</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <p className="font-bold mb-2">For Provider:</p>
                <div className="space-y-2">
                  <p>
                    <strong>Name:</strong> Atsu Emmanuel
                  </p>
                  <p>
                    <strong>Title:</strong> CTO
                  </p>
                  <p>
                    <strong>Date:</strong> {new Date().toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div>
                <p className="font-bold mb-2">For Customer:</p>
                <div className="space-y-2">
                  <p>
                    <strong>Name:</strong>{" "}
                    {biz?.representativeName || "[Representative Name]"}
                  </p>
                  <p>
                    <strong>Title:</strong>{" "}
                    {biz?.representativeRole || "[Representative Title]"}
                  </p>
                  <p>
                    <strong>Date:</strong> {new Date().toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 border-t pt-4">
            <h3 className="text-lg font-bold mb-2">
              Annex 1: Security Measures
            </h3>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>
                <strong>Encryption:</strong> Data at rest (AES-256) and in
                transit (TLS 1.3 or higher).
              </li>
              <li>
                <strong>Access Controls:</strong> Role-based access control
                (RBAC), multi-factor authentication (MFA), IP whitelisting.
              </li>
              <li>
                <strong>Monitoring:</strong> Continuous logging, intrusion
                detection, annual penetration testing.
              </li>
              <li>
                <strong>Physical Security:</strong> Secure, ISO 27001-compliant
                data centers.
              </li>
              <li>
                <strong>Incident Response:</strong> 24/7 monitoring, 72-hour
                breach notification, documented plan.
              </li>
            </ul>

            <h3 className="text-lg font-bold mb-2">
              Annex 2: Authorized Sub-Processors
            </h3>
            <p>
              Provider will notify Customer of any new Sub-Processors or changes
              30 days in advance.
            </p>
            <p className="mb-5">
              Customer may object in writing; if objection is reasonable,
              Provider will either not use the Sub-Processor or allow Customer
              to terminate without penalty.
            </p>

            <OrganizationAgreementCheckbox
              organizationId={organizationId ?? ""}
              initialChecked={false}
              onUpdateSuccess={handleUpdateSuccess}
              onUpdateError={handleUpdateError}
              className="border border-[gray]/20 mb-4"
            />
          </div>
        </>
      ),
    },
    {
      title: "Master Services Agreement (MSA)",
      content: (
        <>
          <p className="text-gray-600 mb-4">
            This Master Services Agreement ("Agreement") is made effective as of{" "}
            {new Date().toLocaleDateString()} ("Effective Date") by and between:
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-5">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h2 className="text-lg font-bold mb-2">Provider:</h2>
              <p>25th & Staffing Limited</p>
              <p>
                Architect Place, 2 Idowu Taylor Street, Victoria Island, Lagos
              </p>
              <p>(trading as 25th & Staffing)</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h2 className="text-lg font-bold mb-2">Customer:</h2>
              <p>{biz?.name || "[Customer Name]"}</p>
            </div>
          </div>

          <p className="mb-4">
            Provider and Customer may be referred to individually as a "Party"
            and collectively as the "Parties."
          </p>

          <h3 className="text-lg font-bold mb-2">1. Services</h3>
          <p className="mb-4">
            Provider agrees to provide Customer with access to its proprietary
            platform known as the 25th Suite ("Services") comprising AI
            Recruiter, 25th Direct, 25th Console, and Employee Lifecycle and any
            related implementation, training, or support services ("Professional
            Services") as detailed in one or more executed Subscription Orders
            or Scopes of Work (SOWs). All Subscription Orders and SOWs shall be
            governed by the terms of this Agreement.
          </p>

          <h3 className="text-lg font-bold mb-2">2. Fees and Payment</h3>
          <div className="space-y-2 mb-4">
            <p>
              <strong>2.1 Fees:</strong> Customer shall pay the fees stated in
              each Subscription Order or as published on Provider's website at
              the time of purchase. All fees are non-refundable except as
              expressly provided in this Agreement or the SLA.
            </p>
            <p>
              <strong>2.2 Invoicing:</strong> Fees will be invoiced in Nigerian
              Naira or agreed currency as outlined in the Subscription Order.
              Unless otherwise specified: Subscription fees are invoiced monthly
              or annually in advance. One-time setup or integration fees are
              invoiced upon execution of the Subscription Order.
            </p>
            <p>
              <strong>2.2.1 VAT:</strong> All fees are exclusive of Value Added
              Tax (VAT). VAT at the prevailing statutory rate shall be added to
              all taxable services and invoiced accordingly.
            </p>
            <p>
              <strong>2.3 Payment:</strong> Payment shall be made as follows:
              70% upfront upon execution of the Subscription Order or receipt of
              invoice, with the remaining 30% due upon go-live or milestone
              completion as specified.
            </p>
            <div>
              <strong>2.4 Non-Payment Remedies:</strong> If any undisputed
              invoice is not paid within 30 days of the due date, Provider may:
              <ul className="list-disc pl-6 mt-1 space-y-1">
                <li>
                  Charge late interest at 1.5% per month (or the maximum rate
                  permitted by law, if lower) on the unpaid balance;
                </li>
                <li>
                  Suspend access to the Services until full payment is received;
                </li>
                <li>
                  Terminate the Agreement for material breach if non-payment
                  continues for an additional 15 days after written notice.
                </li>
              </ul>
              <p className="mt-1">
                Where the payment structure includes a milestone-based balance
                (e.g., the remaining 30% due upon go-live or completion of
                Professional Services), non-payment of that balance after the
                milestone has been achieved and accepted (or deemed accepted)
                shall entitle Provider to immediately suspend or terminate
                access without further notice, without refund, and without
                liability for termination fees. Suspension or termination under
                this section shall not relieve Customer of its obligation to pay
                all outstanding fees, including late interest on the unpaid
                balance.
              </p>
            </div>
          </div>

          <h3 className="text-lg font-bold mb-2">3. Term and Termination</h3>
          <div className="space-y-2 mb-4">
            <p>
              <strong>3.1 Term:</strong> This Agreement begins on the Effective
              Date and continues until terminated.
            </p>
            <p>
              <strong>3.2 Subscription Term and Renewals:</strong> The initial
              subscription term will be stated in the Subscription Order.
              Subscriptions automatically renew for successive terms of equal
              length unless either Party provides written notice of non-renewal
              at least 30 days prior to the end of the current term.
            </p>
            <p>
              <strong>3.3 Termination:</strong> Either Party may terminate this
              Agreement for material breach if the breaching Party fails to cure
              within 30 days of written notice. Upon termination, Customer's
              access to the Services will cease, and Provider will make Customer
              Data available for export for 30 days.
            </p>
          </div>

          <h3 className="text-lg font-bold mb-2">4. Intellectual Property</h3>
          <div className="space-y-2 mb-4">
            <p>
              <strong>4.1 Ownership:</strong> Provider retains all rights,
              title, and interest in the Services, including all intellectual
              property rights. Customer is granted a limited, non-exclusive,
              non-transferable license to use the Services during the term,
              solely for internal business purposes.
            </p>
            <p>
              <strong>4.2 Customer Data:</strong> Customer retains ownership of
              its data. Provider may use anonymized Customer Data for improving
              the Services.
            </p>
          </div>

          <h3 className="text-lg font-bold mb-2">5. Confidentiality</h3>
          <div className="space-y-2 mb-4">
            <p>
              <strong>5.1 Definition:</strong> "Confidential Information" means
              all non-public information disclosed by one Party to the other,
              including business plans, technical data, financial information,
              customer lists, and any other proprietary information.
            </p>
            <p>
              <strong>5.2 Obligations:</strong> Each Party (the "Receiving
              Party") shall hold in confidence all Confidential Information
              disclosed by the other Party ("Disclosing Party"). The Receiving
              Party shall use such Confidential Information only for the purpose
              of performing its obligations under this Agreement and shall not
              disclose it to any third party without the Disclosing Party's
              prior written consent.
            </p>
            <p>
              <strong>5.3 Exceptions:</strong> Confidential Information does not
              include information that is publicly known, independently
              developed, or rightfully obtained from a third party.
            </p>
            <p>
              <strong>5.4 Duration:</strong> Confidentiality obligations survive
              termination for 5 years.
            </p>
          </div>

          <h3 className="text-lg font-bold mb-2">6. Data and Security</h3>
          <div className="space-y-2 mb-4">
            <p>
              <strong>6.1 Security:</strong> Provider shall implement and
              maintain appropriate technical and organizational security
              measures to protect Customer Data, as detailed in the Service
              Level Agreement (Exhibit B).
            </p>
            <p>
              <strong>6.2 Data Privacy:</strong> All data processing shall be
              governed by the Data Processing Addendum (Exhibit B), which is
              hereby incorporated by reference.
            </p>
            <p>
              <strong>6.3 Prohibition on Data Sale or Misuse:</strong> Neither
              Party, nor their employees, officers, agents, affiliates, or
              subcontractors, shall sell, rent, trade, lease, share for
              remuneration, or otherwise intentionally misuse, disclose, or
              commercially exploit the other Party’s Confidential Information,
              candidate data, employee records, or any Personal Data processed
              under this Agreement.
            </p>
            <div className="pl-4 border-l-2 border-gray-500 p-2 my-2 rounded">
              <strong>Breach & Remedies:</strong> In the event of a proven
              breach of this clause by a Party (including willful or intentional
              sale, trade, or misuse of data):
              <ul className="list-disc pl-6 mt-1 space-y-1">
                <li>
                  Immediate termination of this Agreement for cause, without
                  notice or liability for termination fees;
                </li>
                <li>
                  Liquidated damages of NGN 25,000,000 per unauthorized
                  disclosure or misuse incident (or the equivalent in USD at the
                  prevailing parallel market exchange rate on the date of the
                  breach), which the Parties agree is a reasonable pre-estimate
                  of harm;
                </li>
                <li>
                  Full indemnification for direct and reasonably foreseeable
                  losses, including regulatory fines, third-party claims,
                  remediation/notification costs, and reasonable legal fees.
                </li>
              </ul>
              <p className="mt-1">
                Liability for willful or intentional breach under this clause
                shall not be subject to any limitation of liability in this
                Agreement and shall survive termination.
              </p>
            </div>
            <p className="mt-2 text-sm text-gray-600">
              <strong>Standard Liability Cap:</strong> Except for breaches under
              this clause, each Party’s total aggregate liability under this
              Agreement shall be capped at 100% of the fees paid or payable by
              the Customer in the twelve (12) months preceding the claim.
            </p>
          </div>

          <h3 className="text-lg font-bold mb-2">
            7. Warranties and Disclaimers
          </h3>
          <div className="space-y-2 mb-4">
            <p>
              <strong>7.1 Warranties:</strong> Provider warrants that the
              Services will perform materially in accordance with the applicable
              documentation and the Service Level Agreement (Exhibit A).
            </p>
            <p>
              <strong>7.2 Disclaimer:</strong> EXCEPT AS EXPRESSLY PROVIDED
              HEREIN, PROVIDER MAKES NO WARRANTIES OF ANY KIND, WHETHER EXPRESS
              OR IMPLIED (INCLUDING ANY WARRANTY OF MERCHANTABILITY OR FITNESS
              FOR A PARTICULAR PURPOSE).
            </p>
          </div>

          <h3 className="text-lg font-bold mb-2">8. Indemnification</h3>
          <p className="mb-4">
            Each Party shall indemnify, defend, and hold harmless the other
            Party from and against any third-party claims, losses, damages,
            liabilities, costs, and expenses (including reasonable attorneys'
            fees) arising from or relating to: (i) the indemnifying Party's
            breach of this Agreement, (ii) gross negligence or willful
            misconduct, or (iii) violation of applicable laws.
          </p>

          <h3 className="text-lg font-bold mb-2">9. Limitation of Liability</h3>
          <div className="bg-gray-50 p-3 rounded mb-4 text-sm font-medium">
            <p className="mb-2">
              IN NO EVENT SHALL EITHER PARTY'S AGGREGATE LIABILITY ARISING OUT
              OF THIS AGREEMENT EXCEED THE TOTAL FEES PAID OR PAYABLE BY
              CUSTOMER TO PROVIDER DURING THE TWELVE (12) MONTHS PRECEDING THE
              CLAIM.
            </p>
            <p>
              IN NO EVENT SHALL EITHER PARTY BE LIABLE FOR ANY LOST PROFITS,
              INDIRECT, SPECIAL, INCIDENTAL, OR CONSEQUENTIAL DAMAGES. (The
              above is subject to exceptions in Section 6.3 for data misuse.)
            </p>
          </div>

          <h3 className="text-lg font-bold mb-2">10. Force Majeure</h3>
          <p className="mb-4">
            Neither Party shall be liable for any delay or failure to perform
            resulting from causes outside its reasonable control, including acts
            of God, natural disasters, war, terrorism, civil unrest, national
            grid collapse, or major internet service provider failures ("Force
            Majeure Event").
            <br />
            <strong>Notification:</strong> The affected party must notify the
            other within 48 hours.
            <br />
            <strong>Extended Event:</strong> If the Force Majeure Event
            continues for more than 30 days, either party may terminate the
            agreement without penalty (prorated refunds apply for unused
            services).
          </p>

          <h3 className="text-lg font-bold mb-2">11. General Provisions</h3>
          <div className="space-y-2 mb-4">
            <p>
              <strong>11.1 Governing Law:</strong> This Agreement shall be
              governed by and construed in accordance with the laws of the
              Federal Republic of Nigeria.
            </p>
            <p>
              <strong>11.2 Entire Agreement:</strong> This Agreement, including
              all Exhibits, Subscription Orders, and SOWs, constitutes the
              entire agreement between the Parties and supersedes all prior
              proposals and understandings.
            </p>
            <p>
              <strong>11.3 Assignment:</strong> Neither Party may assign this
              Agreement without the other Party's prior written consent, except
              in connection with a merger, acquisition, or sale of all or
              substantially all assets.
            </p>
            <p>
              <strong>11.4 Notices:</strong> All notices shall be in writing and
              sent to the addresses specified in this Agreement.
            </p>
            <p>
              <strong>11.5 Severability:</strong> If any provision of this
              Agreement is held invalid or unenforceable, the remaining
              provisions shall remain in full force.
            </p>
            <p>
              <strong>11.6 Waiver:</strong> No waiver of any term shall be
              deemed a further or continuing waiver.
            </p>
            <p>
              <strong>11.7 Counterparts:</strong> This Agreement may be executed
              in counterparts, each of which shall be deemed an original.
            </p>
            <p>
              <strong>11.8 Survival:</strong> Sections 5 (Confidentiality), 6
              (Data and Security), 8 (Indemnification), 9 (Limitation of
              Liability), and 11 (General Provisions) shall survive termination.
            </p>
          </div>

          <div className="mt-6 space-y-2 text-sm text-gray-600">
            <p>
              <strong>Exhibit A:</strong> Service Level Agreement (SLA) [Insert
              SLA details here – uptime, support, credits, etc.]
            </p>
            <p>
              <strong>Exhibit B:</strong> Data Processing Addendum (DPA) [Insert
              DPA details here – data processing roles, sub-processors, security
              measures, breach notification, etc.]
            </p>
            <p>
              <strong>Exhibit C:</strong> Pricing Schedule [Insert pricing table
              link here – fees, add-ons, per-company pricing, etc.]
            </p>
          </div>

          <div className="border-t pt-4 mt-8">
            <p className="mb-4">
              This Agreement is executed by the undersigned, who represent that
              they are duly authorized to bind their respective Party.
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <p className="font-bold mb-2">
                  For and on behalf of the Provider: 25th & Staffing
                </p>
                <div className="space-y-2">
                  {/* <p>
                    <strong>By:</strong> ______________________________
                  </p> */}
                  <p>
                    <strong>Name:</strong> Emem Usen
                  </p>
                  <p>
                    <strong>Title:</strong> CEO
                  </p>
                  <p>
                    <strong>Date:</strong> {new Date().toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div>
                <p className="font-bold mb-2">
                  For and on behalf of the Customer:{" "}
                  {biz?.name || "[Customer Name]"}
                </p>
                <div className="space-y-2">
                  {/* <p>
                    <strong>By:</strong> ______________________________
                  </p> */}
                  <p>
                    <strong>Name:</strong>{" "}
                    {biz?.representativeName || "[Representative Name]"}
                  </p>
                  <p>
                    <strong>Title:</strong>{" "}
                    {biz?.representativeRole ||
                      "Managing Director / Chief Executive Officer"}
                  </p>
                  <p>
                    <strong>Date:</strong> {new Date().toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <p className="font-bold mb-2 underline">OPTIONAL</p>
              <p className="mb-4">
                Duly Authorized by Board Resolution [Date] (if applicable)
              </p>
              <p>Witnessed by (optional):</p>
              <div className="mt-2 space-y-2">
                <p>
                  <strong>Name:</strong> ______________________________
                </p>
                <p>
                  <strong>Title:</strong> Company Secretary / Head of Legal
                </p>
                <p>
                  <strong>Date:</strong> {new Date().toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </>
      ),
    },
  ];

  const handleNext = () => {
    if (currentStep < termsContent.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleAccept = () => {
    if (isAccepted) {
      setIsComplete(true);
    }
  };

  // Start again with checkbox checked
  const handleStartAgain = () => {
    setCurrentStep(0);
    setIsAccepted(true);
    setIsComplete(false);
  };

  const progressPercentage = ((currentStep + 1) / termsContent.length) * 100;

  if (isComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#E8F5E9]/30 to-[#E3F2FD]/30 flex items-center justify-center p-6">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-[#4CAF50] rounded-full flex items-center justify-center mx-auto">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-[20px] md:text-[25px]">You're all set</h2>
          <p className="text-gray-600">
            DPA and MSA accepted. <br />
            Click continue to proceed.
            {/* Choose your payment method to continue. */}
          </p>
          <div className="flex justify-center mt-2">
            <Button2
              className="bg-[#2D6EEF] text-[#ffffff] md:h-[50px] md:w-[180px] md:text-[18px]"
              title="Continue"
              onSubmitHandler={() => {
                const params = new URLSearchParams();
                if (id) params.append("Id", id);
                if (tier) params.append("tier", tier);
                if (frequency) params.append("frequency", frequency);
                if (directPlanTier) params.append("directPlan", directPlanTier);
                if (includeSetupFee)
                  params.append("includeSetupFee", includeSetupFee);

                // Add Console params
                const deploymentIds = searchParams.get("deploymentIds");
                const integrationIds = searchParams.get("integrationIds");
                const featureIds = searchParams.get("featureIds");
                const directAddOnIds = searchParams.get("directAddOnIds");

                if (deploymentIds)
                  params.append("deploymentIds", deploymentIds);
                if (integrationIds)
                  params.append("integrationIds", integrationIds);
                if (featureIds) params.append("featureIds", featureIds);
                if (directAddOnIds)
                  params.append("directAddOnIds", directAddOnIds);

                // Determine route based on flow
                // Console (deploymentIds) or Direct (directPlan) -> Order Form
                if (directPlanTier || deploymentIds) {
                  navigate(`/order-form?${params.toString()}`);
                } else {
                  // AI Recruiter -> Pay
                  navigate(`/order-form?${params.toString()}`);
                  // navigate(`/pay?${params.toString()}`);
                }
              }}
            />
          </div>
          <div
            onClick={handleStartAgain}
            className="cursor-pointer text-primary hover:text-[#0B60D6] focus:text-[#0B60D6] flex items-center justify-center text-[16px]"
          >
            <span className="pr-1">View Agreements </span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
              data-rtl-flip=""
            >
              <path d="M11.3349 10.3301V5.60547L4.47065 12.4707C4.21095 12.7304 3.78895 12.7304 3.52925 12.4707C3.26955 12.211 3.26955 11.789 3.52925 11.5293L10.3945 4.66504H5.66011C5.29284 4.66504 4.99507 4.36727 4.99507 4C4.99507 3.63273 5.29284 3.33496 5.66011 3.33496H11.9999L12.1337 3.34863C12.4369 3.41057 12.665 3.67857 12.665 4V10.3301C12.6649 10.6973 12.3672 10.9951 11.9999 10.9951C11.6327 10.9951 11.335 10.6973 11.3349 10.3301ZM11.333 4.66699L11.3349 4.66797L11.332 4.66504H11.331L11.333 4.66699Z"></path>
            </svg>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="font-abel min-h-screen bg-gradient-to-br from-[#f8fafc]/30 to-[#E3F2FD]/30 py-8 px-4">
      <div className="max-w-[1200px] mx-auto">
        <div className="mb-5">
          <h1 className="text-[25px] md:text-[30px] mb-2 text-center">
            Terms and Conditions
          </h1>
          <p className="text-gray-600 text-center mb-6">
            Kindly go through all the three agreement documents and click the
            accept button to proceed.
          </p>
          <Progress value={progressPercentage} className="h-2" />
          <p className="text-sm text-gray-500 text-center mt-2">
            Agreement {currentStep + 1} of {termsContent.length}
          </p>
        </div>

        <Card className="p-4 md:p-5 mb-5 md:h-[480px] h-[380px] overflow-scroll">
          <h2 className="mb-2 text-[16px] md:text-[24px] font-[500] text-center">
            {termsContent[currentStep].title}
          </h2>
          <div className="text-gray-700 leading-relaxed">
            {termsContent[currentStep].content}
          </div>
        </Card>

        {currentStep === termsContent.length - 1 ? (
          <div className="space-y-3">
            <div className="flex items-center space-x-3 px-4 py-2 bg-white rounded-lg border border-[gray]/20">
              <Checkbox
                id="accept-terms"
                checked={isAccepted}
                onCheckedChange={(checked) => setIsAccepted(checked === true)}
              />
              <label
                htmlFor="accept-terms"
                className="text-sm cursor-pointer leading-relaxed"
              >
                I have read and agree to the conditions outlined above.
              </label>
            </div>
            <div className="flex justify-between">
              <Button
                variant="outlined"
                onClick={handlePrevious}
                className="py-2"
              >
                Previous
              </Button>
              <Button
                variant="filled"
                onClick={handleAccept}
                disabled={!isAccepted}
                className="min-w-[140px] bg-[black] text-[white] py-2"
              >
                Accept & Complete
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center space-x-3 px-4 py-2 bg-white rounded-lg border border-[gray]/20">
              <Checkbox
                id="accept-terms"
                checked={isAccepted}
                onCheckedChange={(checked) => setIsAccepted(checked === true)}
              />
              <label
                htmlFor="accept-terms"
                className="text-sm cursor-pointer leading-relaxed"
              >
                I have read and agree to the conditions outlined above.
              </label>
            </div>
            <div className="flex justify-between">
              <Button
                variant="outlined"
                onClick={handlePrevious}
                disabled={currentStep === 0}
                className="py-2"
              >
                Previous
              </Button>
              <Button
                disabled={!isAccepted}
                onClick={handleNext}
                variant="filled"
                className="bg-[black] text-[white] py-2"
              >
                Accept & Continue
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TermsAcceptance;
